import type { DisplayContact } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { Pressable as Slot_Pressable } from '@rn-primitives/slot'
import dayjs from 'dayjs'
import { Link, type Href } from 'expo-router'
import { useMemo } from 'react'
import { Pressable, View, type GestureResponderEvent } from 'react-native'
import Animated from 'react-native-reanimated'
import Icon from '~/components/Icon'
import { Label } from '~/components/typography/Content'
import { Heading2 } from '~/components/typography/Titles.tsx'
import { ButtonLabel, FakeButton } from '~/components/ui/button'
import { Text } from '~/components/ui/text'
import { cn } from '~/lib/utils'

interface LeadListItemProps {
  contact: DisplayContact
  onSelect?: (
    event: GestureResponderEvent | React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => void
  showAssetName?: boolean
  showFeedbackStatus?: boolean
  className?: string
  buttonLabel?: string
  isSelected?: boolean
  linkToContact?: boolean | Href
  buttonVariant?: 'default' | 'outline' | 'ghost'
  isDisabled?: boolean
}

const AnimatedPressable = Animated.createAnimatedComponent(Pressable)

export function ContactListItem({
  contact,
  onSelect,
  linkToContact,
  showAssetName,
  showFeedbackStatus = true,
  className,
  buttonLabel,
  isSelected,
  buttonVariant = 'outline',
  isDisabled = false,
}: LeadListItemProps) {
  const { t } = useTranslations('contacts')
  const contactDetails = [contact.phone, contact.email].filter(Boolean)

  const isInactive =
    contact.disabled_at !== null &&
    contact.disabled_at !== undefined &&
    contact.disabled_reason === 'INACTIVE'

  const isReserved =
    contact.disabled_at !== null &&
    contact.disabled_at !== undefined &&
    contact.disabled_reason === 'RESERVED'

  const dateAdded = useMemo(() => {
    // eslint-disable-next-line i18next/no-literal-string
    const dateFormat = 'DD.MM.YYYY HH:mm'
    return dayjs(contact.assets?.[0]?.created_at ?? contact.created_at).format(dateFormat)
  }, [contact])

  const ContentContainer = onSelect || linkToContact ? AnimatedPressable : Animated.View

  const content = (
    <ContentContainer
      className={cn(
        'group/button native:shadow-card disabled:scale-100! disabled:bg-background! mx-2 my-1 flex-row items-start justify-between rounded-xl bg-background px-4 py-3 shadow-md shadow-primary/20 transition-all duration-150 hover:bg-muted active:scale-[0.98] active:bg-muted',
        className
      )}>
      <View className="flex-1 items-start justify-start gap-4">
        <View className="w-full gap-1">
          <View className="w-full flex-row items-start justify-between gap-2">
            <Heading2 className="flex-1 font-light text-lg">{contact.name}</Heading2>
            {isInactive ? (
              <View className="ml-auto flex-row items-center gap-1.5 rounded-full border border-destructive/40 bg-destructive/10 px-2.5 py-1">
                <Icon name="ban" size={12} className="text-destructive" />
                <Text className="font-semibold text-xs/relaxed text-destructive">
                  {t(($) => $.disabled.inactive)}
                </Text>
              </View>
            ) : isReserved ? (
              <View className="flex-row items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 px-2.5 py-1">
                <Icon name="lock-outline" size={12} className="text-amber-600" />
                <Text className="font-semibold text-xs/relaxed text-amber-700">
                  {t(($) => $.disabled.reserved)}
                </Text>
              </View>
            ) : null}
          </View>

          <View className="flex-row flex-wrap items-center gap-3">
            {contactDetails.slice(0, 2).map((detail) => (
              <Label key={detail} className="font-light text-sm">
                {detail}
              </Label>
            ))}
          </View>

          {showAssetName && contact.assets && contact.assets.length > 0 && (
            <View className="ml-auto items-end justify-start gap-1.5 self-start pt-1">
              <Text className="font-semibold text-xs text-secondary">
                {contact.assets?.[0]?.name}
              </Text>
              <Text className="text-xs text-secondary">{dateAdded}</Text>
            </View>
          )}
        </View>
        <View className="w-full flex-1 flex-row items-center justify-between gap-4">
          <FakeButton
            variant={(isDisabled ? 'ghost' : buttonVariant) || (isSelected ? 'default' : 'outline')}
            size="sm"
            disabled={isDisabled}
            className={cn('pointer-events-none flex-row gap-3 self-start', isDisabled && 'px-0')}>
            <ButtonLabel>
              {buttonLabel || (isSelected ? t(($) => $.flow.selected) : t(($) => $.flow.open))}
            </ButtonLabel>
          </FakeButton>
          <View className="flex-row items-center gap-2">
            {showFeedbackStatus && !isInactive && (
              <View className="flex-row gap-2">
                <Icon
                  name={
                    contact.feedback?.some((feedbackItem) => feedbackItem.completed_at !== null)
                      ? 'message-star-outline'
                      : contact.feedback && contact.feedback.length > 0
                        ? 'message-arrow-left-outline'
                        : 'message-off-outline'
                  }
                  size={24}
                  className={cn(
                    'text-secondary',
                    contact.feedback?.some((feedbackItem) => feedbackItem.completed_at !== null) &&
                      'text-accent'
                  )}
                />
              </View>
            )}
          </View>
        </View>
      </View>
    </ContentContainer>
  )

  if (linkToContact) {
    return (
      <Link
        href={
          typeof linkToContact === 'string'
            ? linkToContact
            : {
                pathname: '/leads/[leadId]',
                params: { leadId: contact.id },
              }
        }
        asChild>
        {content}
      </Link>
    )
  }

  if (!onSelect) {
    return content
  }

  return (
    <Slot_Pressable disabled={isDisabled} onPress={onSelect}>
      {content}
    </Slot_Pressable>
  )
}
